import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiSave } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function compactNumber(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "0";
  const rounded = Math.round(number);
  const sign = rounded < 0 ? "-" : "";
  return `${sign}${String(Math.abs(rounded)).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
}

function formatMoney(value) {
  return `${compactNumber(value)} €`;
}

function inputValue(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number === 0) return "";
  return compactNumber(number);
}

function parseAmountInput(value) {
  const raw = String(value || "").trim();
  if (!raw) return 0;
  const normalized = raw.replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

const TAXONOMY_LEVELS = [
  { value: "family", label: "Family", plural: "families" },
  { value: "subfamily", label: "Subfamily", plural: "subfamilies" },
  { value: "category", label: "Category", plural: "categories" },
  { value: "subcategory", label: "Subcategory", plural: "subcategories" },
];

function buildDrafts(report) {
  const rows = report?.rows || [];
  const days = report?.days || [];
  return rows.reduce((acc, row) => {
    const rowKey = row.taxonomy_key || "__uncategorized__";
    acc[rowKey] = {};
    days.forEach((day) => {
      const cell = row.days?.[day.date] || {};
      acc[rowKey][day.date] = {
        net_sales_forecast: inputValue(cell.net_sales_forecast),
        purchase_cost_forecast: inputValue(cell.purchase_cost_forecast),
      };
    });
    return acc;
  }, {});
}

export default function Forecasting() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const [month, setMonth] = useState(currentMonth);
  const [taxonomyLevel, setTaxonomyLevel] = useState("family");
  const [report, setReport] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadForecast = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setMessage("");
      const res = await axios.get("/api/analytics/forecasts/daily", {
        params: {
          business_unit_id: businessUnitId,
          month,
          taxonomy_level: taxonomyLevel,
        },
      });
      setReport(res.data || null);
      setDrafts(buildDrafts(res.data || null));
      setDirty(false);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load forecast.");
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, month, taxonomyLevel]);

  useEffect(() => {
    loadForecast();
  }, [loadForecast]);

  const days = useMemo(() => report?.days || [], [report]);
  const rows = useMemo(() => report?.rows || [], [report]);
  const currentLevel = TAXONOMY_LEVELS.find((level) => level.value === taxonomyLevel) || TAXONOMY_LEVELS[0];

  const totals = useMemo(() => {
    let sales = 0;
    let cost = 0;
    rows.forEach((row) => {
      const rowKey = row.taxonomy_key || "__uncategorized__";
      days.forEach((day) => {
        const draft = drafts?.[rowKey]?.[day.date] || {};
        sales += parseAmountInput(draft.net_sales_forecast);
        cost += parseAmountInput(draft.purchase_cost_forecast);
      });
    });
    return {
      sales,
      cost,
      margin: sales - cost,
      costPercent: sales ? (cost / sales) * 100 : null,
    };
  }, [days, drafts, rows]);

  const updateCell = (rowKey, date, field, value) => {
    setDrafts((current) => ({
      ...current,
      [rowKey]: {
        ...(current[rowKey] || {}),
        [date]: {
          ...(current[rowKey]?.[date] || {}),
          [field]: value,
        },
      },
    }));
    setDirty(true);
    setMessage("");
  };

  const saveForecast = async () => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const entries = [];
      rows.forEach((row) => {
        const rowKey = row.taxonomy_key || "__uncategorized__";
        days.forEach((day) => {
          const draft = drafts?.[rowKey]?.[day.date] || {};
          entries.push({
            forecast_date: day.date,
            taxonomy_level: taxonomyLevel,
            taxonomy_id: row.taxonomy_id || null,
            taxonomy_key: rowKey,
            net_sales_forecast: parseAmountInput(draft.net_sales_forecast),
            purchase_cost_forecast: parseAmountInput(draft.purchase_cost_forecast),
          });
        });
      });

      const res = await axios.put("/api/analytics/forecasts/daily", {
        business_unit_id: businessUnitId,
        month,
        taxonomy_level: taxonomyLevel,
        entries,
      });
      setReport(res.data || null);
      setDrafts(buildDrafts(res.data || null));
      setDirty(false);
      setMessage("Forecast saved.");
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save forecast.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="reservations-container rm-page analyticsForecastPage">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">Forecasting</h3>
            <div className="rm-rates-subtitle">
              {loading ? "Loading forecast..." : `${currentLevel.plural} / ${month}`}
            </div>
          </div>

          <div className="rm-topActions">
            <select
              className="posTextInput analyticsLevelSelect"
              value={taxonomyLevel}
              onChange={(event) => setTaxonomyLevel(event.target.value || "family")}
            >
              {TAXONOMY_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
            <input
              className="posTextInput analyticsMonthInput"
              type="month"
              value={month}
              onChange={(event) => setMonth(event.target.value || currentMonth())}
            />
            <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={saveForecast} disabled={saving || !dirty}>
              <FiSave /> {saving ? "Saving..." : "Save forecast"}
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="posStateBanner">{error}</div> : null}
      {message ? <div className="posStateBanner analyticsSuccessBanner">{message}</div> : null}

      <div className="posMetricGrid analyticsMetricGrid">
        <div className="posMetricTile">
          <span>Forecast sales</span>
          <strong>{formatMoney(totals.sales)}</strong>
        </div>
        <div className="posMetricTile">
          <span>Forecast cost</span>
          <strong>{formatMoney(totals.cost)}</strong>
        </div>
        <div className="posMetricTile">
          <span>Forecast margin</span>
          <strong>{formatMoney(totals.margin)}</strong>
        </div>
        <div className="posMetricTile">
          <span>Cost %</span>
          <strong>{totals.costPercent == null ? "-" : `${compactNumber(totals.costPercent)}%`}</strong>
        </div>
      </div>

      <div className="rm-rates-card analyticsMatrixCard analyticsForecastMatrixCard">
        <div className="rm-rates-header analyticsCompactHeader">
          <h3 className="rm-rates-title">Daily forecast</h3>
          <div className="rm-rates-subtitle">Sales and cost by POS {currentLevel.label.toLowerCase()}</div>
        </div>

        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">
            {loading ? "Loading matrix..." : `${rows.length} taxonomy rows`}
          </small>
        </div>

        <div className="rm-rates-table-wrap analyticsMatrixWrap">
          <table
            className="rm-rates-table analyticsMatrixTable analyticsForecastTable"
            style={{
              "--rm-col1-width": "220px",
              "--rm-table-min-width": `${Math.max(1200, 360 + days.length * 144)}px`,
            }}
          >
            <colgroup>
              <col style={{ width: "220px" }} />
              {days.map((day) => (
                <col key={day.date} style={{ width: "144px" }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className="res-th rm-sticky-1">
                  <div className="res-thBtn">
                    <span className="res-thLabel">{currentLevel.label}</span>
                    <span className="res-thSub">POS taxonomy</span>
                  </div>
                </th>
                {days.map((day) => (
                  <th key={day.date} className="res-th">
                    <div className="res-thBtn analyticsDayHeader">
                      <span className="res-thLabel">{day.label}</span>
                      <span className="res-thSub">{month}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rowKey = row.taxonomy_key || "__uncategorized__";
                return (
                  <tr key={rowKey}>
                    <td className="rm-sticky-1 analyticsFamilyName">
                      <div className="analyticsFamilyNameInner">
                        <strong>{row.taxonomy_name}</strong>
                        <span>{row.taxonomy_code}</span>
                      </div>
                    </td>
                    {days.map((day) => {
                      const draft = drafts?.[rowKey]?.[day.date] || {};
                      return (
                        <td key={day.date} className="analyticsForecastInputCell">
                          <label>
                            <span>Sales</span>
                            <input
                              value={draft.net_sales_forecast || ""}
                              inputMode="decimal"
                              onChange={(event) =>
                                updateCell(rowKey, day.date, "net_sales_forecast", event.target.value)
                              }
                            />
                          </label>
                          <label>
                            <span>Cost</span>
                            <input
                              value={draft.purchase_cost_forecast || ""}
                              inputMode="decimal"
                              onChange={(event) =>
                                updateCell(rowKey, day.date, "purchase_cost_forecast", event.target.value)
                              }
                            />
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {!rows.length ? (
                <tr>
                  <td colSpan={days.length + 1} className="posMuted">
                    No taxonomy rows found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
